/**
 * PrintLabelModal — the operator's control panel for label printing.
 *
 * Handles both single-product (`products = [one]`) and batch
 * (`products = [selected...]`) flows. Same UI, same layout — the only
 * difference is the preview cycles through each product and the print
 * loop runs N times.
 *
 * Flow:
 *   1. Load templates from /api/labels/templates (seed defaults on first run).
 *   2. User picks: template + language + copies + font size + QR/barcode ON/OFF.
 *   3. Preview updates live using LabelPreview.
 *   4. User picks Printer (Bluetooth Niimbot OR "System Print Dialog").
 *   5. Print → per-product loop → renders SVG → sends to printer.
 *   6. After each print, POST /api/labels/print-history for audit trail.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Bluetooth, Monitor, Printer, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";

import api from "../lib/api";
import { LabelPreview, renderLabelSVG } from "./LabelPreview";
import { productQRUrl } from "../lib/labelFormat";
import {
  connectNiimbotBluetooth,
  printLabel,
  loadLastPrinter,
  saveLastPrinter,
} from "../lib/printerService";

export default function PrintLabelModal({ products, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("tpl_ring_tag");
  const [language, setLanguage] = useState("en");
  const [copies, setCopies] = useState(1);
  const [fontSize, setFontSize] = useState(null); // null = template default
  const [showQR, setShowQR] = useState(null);
  const [showBarcode, setShowBarcode] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Printer state
  const [printer, setPrinter] = useState(null); // {id, name, kind, ...}
  const [connecting, setConnecting] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Live-generated media (QR + Code128) for the currently-previewed product
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [barcodeSvg, setBarcodeSvg] = useState("");

  const activeProduct = products[activeIndex] || products[0];
  const template = useMemo(
    () => templates.find((t) => t.id === templateId) || templates[0] || null,
    [templates, templateId],
  );

  // ---- Load templates + remember last printer ----
  useEffect(() => {
    api.get("/labels/templates").then(({ data }) => {
      setTemplates(data);
      const last = loadLastPrinter();
      if (last?.kind === "browser") setPrinter(last); // Only browser is safe to auto-select; BLE needs re-pairing
    }).catch((e) => {
      toast.error("Could not load label templates");
      console.error(e);
    });
  }, []);

  // ---- Regenerate QR + Barcode whenever active product changes ----
  useEffect(() => {
    if (!activeProduct) return;
    const url = productQRUrl(activeProduct);
    QRCode.toDataURL(url, { errorCorrectionLevel: "M", margin: 0, scale: 6 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));

    // Code128 SVG via jsbarcode — into an off-screen SVG element, then we
    // extract its innerHTML for the label template.
    try {
      const holder = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      JsBarcode(holder, activeProduct.sku || "OPIK", {
        format: "CODE128",
        displayValue: false,
        margin: 0,
        height: 30,
        width: 1.2,
      });
      setBarcodeSvg(holder.innerHTML);
    } catch (e) {
      setBarcodeSvg("");
    }
  }, [activeProduct]);

  // ---- Printer connection ----
  const connectBluetooth = async (showAll = false) => {
    setConnecting(true);
    try {
      const p = await connectNiimbotBluetooth({ showAll });
      setPrinter(p);
      saveLastPrinter({ id: p.id, name: p.name, kind: "bluetooth", model: p.model });
      toast.success(`Paired ${p.name}`);
    } catch (e) {
      if (e.code === "BT_UNSUPPORTED") {
        toast.error(e.message);
      } else if (e.code === "BT_CANCELLED") {
        // User cancelled the picker — silent
      } else if (e.name === "NotFoundError") {
        toast.error("No Bluetooth printers found. Make sure the printer is powered on and try 'Show all devices'.");
      } else if (e.name === "SecurityError") {
        toast.error("Bluetooth needs a secure (HTTPS) connection + user click. Try again by tapping the button.");
      } else {
        toast.error(`Bluetooth failed: ${e.message || e}`);
      }
    } finally {
      setConnecting(false);
    }
  };

  const pickSystemPrinter = () => {
    const p = { id: "sys:browser", name: "System print dialog", kind: "browser" };
    setPrinter(p);
    saveLastPrinter(p);
  };

  // ---- Print loop ----
  const doPrint = async () => {
    if (!template) return toast.error("Please pick a template");
    if (!printer) return toast.error("Printer not connected.");
    if (!products?.length) return;
    setPrinting(true);

    try {
      for (let i = 0; i < products.length; i += 1) {
        const prod = products[i];
        setActiveIndex(i);

        // Per-product QR + barcode data — must be sync-generated for each
        // print (state timing wouldn't be reliable during a tight loop).
        const qrUrl = productQRUrl(prod);
        const qrPng = await QRCode.toDataURL(qrUrl, {
          errorCorrectionLevel: "M", margin: 0, scale: 6,
        });
        let bc = "";
        try {
          const holder = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          JsBarcode(holder, prod.sku || "OPIK", {
            format: "CODE128", displayValue: false, margin: 0, height: 30, width: 1.2,
          });
          bc = holder.innerHTML;
        } catch { /* ignore — barcode optional */ }

        const svg = renderLabelSVG({
          product: prod,
          template,
          language,
          showQR: showQR ?? template.show_qr,
          showBarcode: showBarcode ?? template.show_barcode,
          fontSizeOverride: fontSize ?? template.font_size_pt,
          qrDataUrl: qrPng,
          barcodeDataUrl: bc,
        });

        if (printer.kind === "bluetooth" && printer.driver) {
          // Direct-BLE binary protocol → rasterize the SVG to a monochrome-
          // ready canvas at the printer's native 203 DPI, then hand off.
          const canvas = await svgToCanvas(
            svg,
            template.width_mm,
            template.height_mm,
            203, // Niimbot B1/B21/B31/B3S native dot pitch
          );
          await printLabel(printer, svg, copies, canvas);
        } else {
          // OS-mediated print dialog (Bluetooth without direct-BLE support,
          // or the "System / USB" pick).
          await printLabel(printer, svg, copies);
        }

        // Audit trail — one row per product (copies field captures qty)
        await api.post("/labels/print-history", {
          product_id: prod.id,
          product_sku: prod.sku,
          product_name: prod.name,
          template_id: template.id,
          template_name: template.name,
          printer_name: printer.name,
          printer_kind: printer.kind,
          copies,
          language,
          status: "success",
        }).catch(() => { /* audit failure isn't user-facing */ });
      }
      toast.success(products.length === 1
        ? `Printed ${copies} label(s)`
        : `Printed ${products.length} products × ${copies} labels`);
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error(`Print failed: ${e.message || e}`);
      // Best-effort failure log — helps debug offline-printer scenarios
      try {
        await api.post("/labels/print-history", {
          product_id: activeProduct?.id || "",
          product_sku: activeProduct?.sku || "",
          product_name: activeProduct?.name || "",
          template_id: template?.id, template_name: template?.name || "",
          printer_name: printer?.name || "unknown",
          printer_kind: printer?.kind || "browser",
          copies, language, status: "failed",
          error_message: String(e.message || e).slice(0, 200),
        });
      } catch { /* ignore */ }
    } finally {
      setPrinting(false);
    }
  };

  if (!template) {
    return (
      <ModalShell onClose={onClose} title="Print Label">
        <div className="text-[#8A8A8A] py-8 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading templates…
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title={products.length === 1 ? "Print Label" : `Print ${products.length} Labels`}>
      <div className="grid md:grid-cols-2 gap-6">
        {/* -------- LEFT: controls -------- */}
        <div className="space-y-5">
          <Field label="Template">
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="label-template-select"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {t.width_mm}×{t.height_mm}mm
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Language">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="luxe-input w-full px-4 py-2.5 text-sm"
                data-testid="label-language-select"
              >
                <option value="en">English</option>
                <option value="id">Bahasa Indonesia</option>
              </select>
            </Field>
            <Field label="Copies">
              <input
                type="number"
                min="1"
                max="200"
                value={copies}
                onChange={(e) => setCopies(Math.max(1, Math.min(200, +e.target.value || 1)))}
                className="luxe-input w-full px-4 py-2.5 text-sm mono"
                data-testid="label-copies-input"
              />
            </Field>
          </div>

          <Field label={`Font size (${fontSize ?? template.font_size_pt}pt)`}>
            <input
              type="range"
              min="5"
              max="20"
              step="1"
              value={fontSize ?? template.font_size_pt}
              onChange={(e) => setFontSize(+e.target.value)}
              className="w-full accent-[#D4AF37]"
              data-testid="label-font-size"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Toggle
              testid="label-toggle-qr"
              label="QR Code"
              value={showQR ?? template.show_qr}
              onChange={(v) => setShowQR(v)}
            />
            <Toggle
              testid="label-toggle-barcode"
              label="Barcode (Code128)"
              value={showBarcode ?? template.show_barcode}
              onChange={(v) => setShowBarcode(v)}
            />
          </div>

          {/* Printer picker */}
          <div className="border border-white/10 p-4 bg-black/20">
            <div className="overline text-[0.55rem] mb-3">Printer</div>
            {printer ? (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {printer.kind === "bluetooth" ? (
                    <Bluetooth className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <Monitor className="w-4 h-4 text-[#7BA585]" />
                  )}
                  <div className="flex flex-col">
                    <span className="text-sm text-[#EAEAEA]">{printer.name}</span>
                    {printer.kind === "bluetooth" && (
                      <span className="text-[0.6rem] mono uppercase tracking-wider text-[#7BA585]">
                        {printer.driver ? "Direct BLE — Niimbot binary protocol" : "OS-paired — using system print"}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setPrinter(null)}
                  className="text-[0.65rem] text-[#8A8A8A] hover:text-[#D4AF37] mono uppercase tracking-wider"
                  data-testid="label-change-printer"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => connectBluetooth(false)}
                    disabled={connecting}
                    className="btn-outline-gold px-3 py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="label-connect-bluetooth"
                  >
                    {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bluetooth className="w-3.5 h-3.5" />}
                    Niimbot (Bluetooth)
                  </button>
                  <button
                    onClick={pickSystemPrinter}
                    className="btn-outline-gold px-3 py-2.5 text-xs inline-flex items-center justify-center gap-2"
                    data-testid="label-pick-system"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                    System / USB
                  </button>
                </div>
                {/* Fallback: some Niimbot firmwares advertise with unexpected
                    names ("B21-xxxxxx" vs "NIIMBOT_xxx"). This button skips
                    the name-prefix filter so the OS chooser shows every BLE
                    device — user can hand-pick their printer.                */}
                <button
                  onClick={() => connectBluetooth(true)}
                  disabled={connecting}
                  className="w-full text-[0.65rem] mono uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37] py-2 disabled:opacity-50"
                  data-testid="label-bluetooth-show-all"
                >
                  Can't see your printer? · Show all Bluetooth devices
                </button>
              </div>
            )}
            <p className="text-[0.65rem] text-[#666] mt-3 leading-relaxed">
              Bluetooth: works with Niimbot B21, B3S, B1, B2, D11, D110, D101 on Chrome/Edge (desktop or Android).
              Make sure your printer is <span className="text-[#D4AF37]">powered on and not connected to the Niimbot phone app</span> — only one host can hold the BLE link at a time.
              System: any OS-installed printer including USB Niimbot, Zebra, Brother, Epson.
            </p>
          </div>
        </div>

        {/* -------- RIGHT: preview + batch navigator + print button -------- */}
        <div className="space-y-4">
          <div className="overline text-[0.55rem] flex items-center justify-between">
            <span>Live Preview</span>
            {products.length > 1 && (
              <span className="mono text-[#D4AF37]">
                {activeIndex + 1} / {products.length}
              </span>
            )}
          </div>

          <div className="bg-white border border-[#D4AF37]/30 p-4 flex items-center justify-center min-h-[220px]">
            {activeProduct && (
              <LabelPreview
                product={activeProduct}
                template={template}
                language={language}
                showQR={showQR}
                showBarcode={showBarcode}
                fontSizeOverride={fontSize}
                qrDataUrl={qrDataUrl}
                barcodeDataUrl={barcodeSvg}
                scale={4}
              />
            )}
          </div>

          {products.length > 1 && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                disabled={activeIndex === 0}
                className="p-2 text-[#8A8A8A] hover:text-[#D4AF37] disabled:opacity-30"
                data-testid="label-preview-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-xs text-[#8A8A8A] truncate flex-1 text-center">
                {activeProduct?.name} · {activeProduct?.sku}
              </div>
              <button
                onClick={() => setActiveIndex((i) => Math.min(products.length - 1, i + 1))}
                disabled={activeIndex === products.length - 1}
                className="p-2 text-[#8A8A8A] hover:text-[#D4AF37] disabled:opacity-30"
                data-testid="label-preview-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          <button
            onClick={doPrint}
            disabled={!printer || printing}
            className="btn-gold w-full py-3 inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            data-testid="label-print-btn"
          >
            {printing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Printing…
              </>
            ) : (
              <>
                <Printer className="w-4 h-4" />
                Print {products.length > 1 ? `${products.length} × ${copies}` : `${copies} label${copies > 1 ? "s" : ""}`}
              </>
            )}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// -------- Small helpers --------
function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-3xl max-h-[92vh] overflow-y-auto fade-up" data-testid="print-label-modal">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Nimbot / Thermal Printer</div>
            <h3 className="serif text-2xl mt-1">{title}</h3>
          </div>
          <button onClick={onClose} data-testid="label-modal-close">
            <X className="w-5 h-5 text-[#8A8A8A]" />
          </button>
        </div>
        <div className="px-7 py-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[0.55rem] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-full px-3 py-2.5 text-xs border transition-colors ${
        value
          ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37]"
          : "border-white/10 bg-black/20 text-[#8A8A8A] hover:border-white/20"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <span>{label}</span>
        <span className="mono text-[0.6rem] uppercase">{value ? "on" : "off"}</span>
      </div>
    </button>
  );
}

/**
 * Rasterise an SVG string → HTMLCanvasElement at the Niimbot head's dot
 * pitch (203 dpi is B1/B21/B31/B3S native — 300 dpi variants only exist
 * in Niimbot's Pro line which we don't officially target). The bitmap is
 * consumed by the direct-BLE driver in `niimbotDriver.js`.
 */
async function svgToCanvas(svgHtml, wMm, hMm, dpi = 203) {
  const wPx = Math.round((wMm / 25.4) * dpi);
  const hPx = Math.round((hMm / 25.4) * dpi);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const blob = new Blob([svgHtml], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = wPx;
      canvas.height = hPx;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, wPx, hPx);
      ctx.drawImage(img, 0, 0, wPx, hPx);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("SVG raster failed"));
    };
    img.src = url;
  });
}
